import React, { useEffect, useMemo, useRef } from 'react';

/**
 * AdSense Advertisement Component
 * Strategic placement for Google AdSense ads throughout the application
 * Features brand-consistent styling and responsive design
 */
const AdComponent = ({
  placement = 'default',
  size = 'medium',
  className = ''
}) => {
  // Environment-based rendering
  const isDevelopment = process.env.NODE_ENV === 'development';

  const adRef = useRef(null);
  // Size configurations for different ad formats
  const sizeConfig = {
    small: {
      width: '320px',
      height: '100px',
      format: 'horizontal'
    },
    medium: {
      width: '728px',
      height: '90px',
      format: 'leaderboard'
    },
    large: {
      width: '300px',
      height: '250px',
      format: 'rectangle'
    },
    responsive: {
      width: '100%',
      height: 'auto',
      format: 'auto'
    }
  };

  const placementSlots = useMemo(() => ({
    default: process.env.REACT_APP_ADSENSE_SLOT_DEFAULT,
    'below-search': process.env.REACT_APP_ADSENSE_SLOT_BELOW_SEARCH,
    'between-recipes': process.env.REACT_APP_ADSENSE_SLOT_BETWEEN_RECIPES,
    'bottom-results': process.env.REACT_APP_ADSENSE_SLOT_BOTTOM_RESULTS
  }), []);

  const adClient = process.env.REACT_APP_ADSENSE_CLIENT_ID || 'ca-pub-3187572158588519';
  const adSlot = placementSlots[placement] || placementSlots.default || '';
  const config = sizeConfig[size] || sizeConfig.medium;

  const canRenderAdUnit = !isDevelopment && Boolean(adClient) && Boolean(adSlot);

  useEffect(() => {
    if (!canRenderAdUnit || typeof window === 'undefined') return;

    const adElement = adRef.current;
    if (adElement?.getAttribute('data-adsbygoogle-status') === 'done') return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      // Ignore duplicate-push race errors to keep ad loading resilient in SPA re-renders.
    }
  }, [canRenderAdUnit, placement, size, adSlot]);

  if (!canRenderAdUnit) {
    return null;
  }
  return (
    <div className="ad-wrapper my-4 flex justify-center">
      <div className={`ad-container ${className}`}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', width: config.width, height: config.height, maxWidth: '100%' }}
          data-ad-client={adClient}
          data-ad-slot={adSlot}
          data-ad-format={config.format}
          data-full-width-responsive={size === 'responsive' ? 'true' : 'false'}
        ></ins>
      </div>
    </div>
  );
};

export default AdComponent;